import { Component, Output, EventEmitter, NgZone, effect, signal, computed, ChangeDetectorRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { ActivatedRoute, Router } from '@angular/router';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { FieldContextDirective } from '@app/common-codes/components/directives/FieldContext.directive';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { Visitor } from '@app/common-codes/models/matwins-tenant/Visitor.model';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';
import { Feedback } from '@app/common-codes/models/matwins-tenant/Feedback.model';
import { BubbleSmileyComponent } from '@app/common-codes/components/nested-components/bubble-smiley/bubble-smiley.component';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { DateTimePickerComponent } from '@app/common-codes/components/nested-components/date-time-picker/date-time-picker.component';
import { AccessCard } from '@app/common-codes/models/matwins-tenant/AccessCard.model';

@Register(Visit)
@Component({
  selector: 'app-visit-detail',
  templateUrl: './visit-detail.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, TranslatePipe, OnReadyDirective, ReactiveBadgeComponent, FieldWrapperComponent, BubbleSmileyComponent, MultiSelectComponent, DateTimePickerComponent, FieldContextDirective]
})
export class VisitDetailComponent extends GenetricDetailComponent<Visit> {
  override ComponentEntityClass = Visit;
  Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  @Output() itemSaved = new EventEmitter<Visit>();

  // Lookups
  visitStatusLookup = signal<LookupItem[]>([]);
  pointOfServiceLookup = signal<LookupItem[]>([]);
  accessCardLookup = signal<LookupItem[]>([]);
  employeeLookup = signal<LookupItem[]>([]);

  visitorTypeOptions: LookupItem[] = [
    { id: '1', key: '1', value: 'Visitor', visible: true, additionalData: {} },
    { id: '2', key: '2', value: 'Contractor', visible: true, additionalData: {} },
    { id: '3', key: '3', value: 'Employee', visible: true, additionalData: {} }
  ];

  // Valeurs dérivées (feedback et signature)
  suggestion = signal<string | null>('');
  comments = signal<string | null>('');
  signatureUrl = signal<string | null>(null);

  isPosSelected = computed(() => !!this.editingEntity()?.selectedPK_Pos?.length);

  rootUrl = '/Documents/';
  lastLoadedEntityId: number | null = null;

  // Nouvelle propriété pour stocker les clés des cartes sélectionnées
  selectedAccessCardKeys = signal<string[]>([]);

  // Les traductions arrivent par RxJS (pas un signal) : sans cette lecture,
  // un computed évalué avant la fin du POST /translations fige l'anglais.
  private translationsRev = toSignal(this.translationService.translations$);

  filteredAccessCardLookup = computed(() => {
    this.translationsRev();
    const accessCardLookup = this.accessCardLookup();
    const entity = this.editingEntity();
    const fkPointOfServiceId = this.matwinsVariables?.posId || entity?.fK_PointOfService;
    const filtered = !accessCardLookup || !fkPointOfServiceId || !entity
      ? accessCardLookup
      : accessCardLookup.filter(acc =>
        Number(acc.additionalData['fk_pos']) === Number(fkPointOfServiceId)
        && acc.additionalData['isrevoked'] !== true); // exclure les cartes révoquées
    // Statut de la carte affiché avec son nom ("CARD-001 (Libre)").
    // On clone les items : le cache lookup garde la valeur brute utilisée
    // pour résoudre attachedCards par nom.
    return filtered?.map(acc => {
      const statusName: string = acc.additionalData?.['accesscardstatusname'];
      return {
        ...acc,
        value: statusName
          ? `${acc.value} (${this.translateAccessCardStatus(statusName)})`
          : acc.value
      };
    });
  });

  // Traduit un nom de statut de carte via _Free-Translations
  // (même convention que le backend : minuscules + tirets).
  // Retourne le nom brut si aucune traduction n'existe.
  private translateAccessCardStatus(statusName: string): string {
    const normalized = statusName.trim().toLowerCase().replace(/\s+/g, '-');
    const key = `_Free-Translations.${normalized}`;
    const translated = this.translationService.instant(key);
    return translated === key ? statusName : translated;
  }

  filteredEmployeeLookup = computed(() => {
    const employeeLookup = this.employeeLookup();
    const entity = this.editingEntity();
    const selectedPosId = entity?.fK_PointOfService;
    if (!employeeLookup || !selectedPosId || !entity) {
      return employeeLookup;
    }
    const result = employeeLookup.filter(emp =>
      `${emp.additionalData['pos_ids']}`.split(',').some(st => st.trim() == `${selectedPosId}`)
    );
    return result;
  });

  constructor(
    route: ActivatedRoute,
    router: Router,
    translationService: TranslationService,
    validationService: GenericValidationService,
    alertModalService: AlertModalService,
    ngZone: NgZone
  ) {
    super(route, router, translationService, validationService, ngZone);
    this.alertModalService = alertModalService;


    effect(() => {
      const entity = this.editingEntity();
      if (entity) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        this.visitStatusLookup.set(this.lookupService.getLookupData('VisitStatus'));
        this.pointOfServiceLookup.set(this.getLookupData('PointOfService'));
        this.accessCardLookup.set(this.getLookupData('AccessCard'));
        let emplLookup = this.getLookupData('Employee');
        this.employeeLookup.set(emplLookup);

        // Feedback / cartes : UNIQUEMENT si changement d'entité
        if (this.lastLoadedEntityId !== entity.id) {
          this.lastLoadedEntityId = entity.id ?? null;
          this.loadFeedbackAndSignature(entity);
          this.loadEmployee();
          this.loadAccessCards(entity); // Charger les cartes sélectionnées
        }

        // POS / Employee : synchroniser si besoin (idempotent)
        this.loadPos();
      } else {
        this.lastLoadedEntityId = null;
      }
    });
  }

  override enterEditMode(): void {
    this.lastLoadedEntityId = null;
    super.enterEditMode();
  }

  // Charge les cartes sélectionnées à partir de accessCards et de la traçabilité attachedCards
  private loadAccessCards(visit: Visit) {
    const keysFromAccessCards = (visit.accessCards ?? [])
      .filter(card => card && card.id != null)
      .map(card => card.id.toString());

    const attachedNames = (visit.attachedCards || '')
      .split(',')
      .map(s => s.trim())
      .filter(s => s !== '');

    const posId = this.matwinsVariables?.posId || visit.fK_PointOfService;
    const allCards = this.accessCardLookup() ?? [];
    const posCards = posId
      ? allCards.filter(acc => Number(acc.additionalData?.['fk_pos']) === Number(posId))
      : allCards;

    const keysFromAttached = attachedNames.map(name => {
      const match = posCards.find(c => c.value?.trim() === name)
        ?? allCards.find(c => c.value?.trim() === name);
      return match ? match.key.toString() : name;
    });

    const keys = [...new Set([...keysFromAccessCards, ...keysFromAttached])];
    this.selectedAccessCardKeys.set(keys);
  }

  loadPos() {
    const entity = this.editingEntity();
    if (!entity) return;
    const selected = entity.fK_PointOfService;
    if (!selected) return;
    if (!entity.selectedPK_Pos || entity.selectedPK_Pos.length === 0) {
      entity.selectedPK_Pos = [selected.toString()];
    }
  }

  loadEmployee() {
    const entity = this.editingEntity();
    if (!entity) return;
    const selected = entity.fK_Employee;
    if (!selected) return;
    if (!entity.selectedPK_Employee || entity.selectedPK_Employee.length === 0) {
      entity.selectedPK_Employee = [selected.toString()];
    }
  }

  private loadFeedbackAndSignature(visit: Visit) {
    // Récupérer le premier feedback
    if (visit.feedbacks && visit.feedbacks.length > 0) {
      const fb = visit.feedbacks[0];
      this.suggestion.set(fb.score?.toString() ?? null);
      this.comments.set(fb.comment ?? '');
    } else {
      this.suggestion.set(null);
      this.comments.set('');
    }

    // Récupérer l'URL de la signature
    const signature = visit.imageSignatureEngagement;
    this.signatureUrl.set(signature?.thumbnail_Path ?? signature?.rawData_Path ?? null);
  }

  onPOSselectChange(evt: string[]) {
    const visit = this.editingEntity();
    if (!visit) return;

    const selectedPosIds = (evt ?? [])
      .map(k => Number(k))
      .filter(n => Number.isFinite(n));

    visit.fK_PointOfService = selectedPosIds[0];
    visit.selectedPK_Pos = evt;

    // Réinitialiser les sélections dépendantes
    if (visit.fK_Employee) {
      visit.fK_Employee = undefined;
      visit.selectedPK_Employee = [];
      visit.employee = undefined;
    }
    // Réinitialiser les cartes d'accès
    visit.accessCards = [];
    this.selectedAccessCardKeys.set([]);
  }

  onHostselectChange(evt: string[]) {
    const visit = this.editingEntity();
    if (!visit) return;

    const selectedHostsIds = (evt ?? [])
      .map(k => Number(k))
      .filter(n => Number.isFinite(n));

    visit.fK_Employee = selectedHostsIds[0];
    visit.selectedPK_Employee = evt;
  }

  // pour gérer la sélection des cartes d'accès
  onAccessCardSelectChange(evt: string[]) {
    const visit = this.editingEntity();
    if (!visit) return;

    this.selectedAccessCardKeys.set(evt);

    // Une visite terminée (Out) ne détient pas la carte :
    // pas de FK_CurrentVisit ni de statut Unavailable —
    // seule la traçabilité attachedCards est tenue à jour.
    const outStatusKey = this.visitStatusLookup().find(s => s.value?.toLowerCase() === 'out')?.key;
    const isVisitOut = outStatusKey != null && String(visit.status) === String(outStatusKey);

    const selectedCardIds = evt.map(k => Number(k)).filter(n => Number.isFinite(n));
    const allCards = this.accessCardLookup();

    visit.accessCards = selectedCardIds.map(id => {
      const lookupItem = allCards.find(card => Number(card.key) === id);
      const card = new AccessCard();
      card.id = id;
      card.iD_SP = lookupItem?.value || '';
      card.fK_CurrentVisit = isVisitOut ? undefined : (visit.id || undefined);
      card.fK_POS = visit.fK_PointOfService;
      card.status = lookupItem?.additionalData?.['status'] ? Number(lookupItem.additionalData['status']) : 0;
      return card;
    });

    // attachedCards = liste assignée complète : la sélection fait foi.
    // Une carte désélectionnée est retirée de la traçabilité (erreur
    // d'affectation, carte cassée/remplacée). On conserve seulement les IDs
    // historiques absents du lookup (carte supprimée, autre POS…) puisqu'ils
    // ne peuvent pas être désélectionnés.
    const lookupValues = new Set(allCards.map(c => c.value));
    const unresolvableIds = (visit.attachedCards || '')
      .split(',').map(s => s.trim())
      .filter(s => s !== '' && !lookupValues.has(s));
    const selectedIds = visit.accessCards.map(c => c.iD_SP).filter(s => s !== '');
    visit.attachedCards = [...new Set([...unresolvableIds, ...selectedIds])].join(', ');


    // Forcer la détection de changement
    this.editingEntity.set(visit);
  }

  updateVisitorProperty(value: any, property: string) {
    const visit = this.editingEntity();
    if (!visit) return;
    if (!visit.visitor) {
      visit.visitor = new Visitor();
    }
    (visit.visitor as any)[property] = value;
    this.editingEntity.set(visit);
  }

  override getEmptyEntity(): Visit {
    const visit = new Visit();
    visit.status = 1; // In
    visit.visitorType = 1; // Visitor
    visit.startDate = new Date();
    visit.numberOfNotificationSent = 0;
    visit.visitor = new Visitor();
    visit.accessCards = []; // Initialiser la collection vide
    if (this.matwinsVariablesService.posId) {
      visit.fK_PointOfService = this.matwinsVariablesService.posId ?? 0;
    }
    return visit;
  }

  override parseToEntityInstance(): void {
    if (this.EntityInstance && this.editingEntity()) {
      Object.assign(this.EntityInstance, this.editingEntity());
      if (this.editingEntity()?.visitor) {
        this.EntityInstance.visitor = this.editingEntity()?.visitor;
      }
      if (this.editingEntity()?.accessCards) {
        this.EntityInstance.accessCards = this.editingEntity()?.accessCards;
      }
    }
  }

  private syncFeedbackToEntity(): void {
    const visit = this.editingEntity();
    if (!visit) return;
    if (this.suggestion() != null && this.suggestion() !== '0') {
      if (!visit.feedbacks || visit.feedbacks.length === 0) {
        visit.feedbacks = [new Feedback()];
        visit.feedbacks[0].fK_Visit = visit.id;
        visit.feedbacks[0].fK_PointOfService = visit.fK_PointOfService;
      }
      const fb = visit.feedbacks[0];
      fb.score = this.suggestion() ? parseInt(this.suggestion()!, 10) : 0;
      fb.comment = this.comments() || '';
    }
  }

  private cleanNavigationBackReferences(): void {
    const entity = this.editingEntity();
    if (!entity) return;

    if (entity.visitor) {
      entity.visitor.visits = undefined;
    }
    if (entity.employee) {
      (entity as any).employee.visits = undefined;
    }
    // Nettoyer les références circulaires des cartes
    if (entity.accessCards) {
      entity.accessCards.forEach(card => {
        card.currentVisit = undefined;
      });
    }
  }

  async saveItem(): Promise<void> {
    this.syncFeedbackToEntity();
    this.cleanNavigationBackReferences();

    const savedItem = await this.save();
    if (savedItem) {
      this.itemSaved.emit(savedItem);
    }
  }

  async saveAndCloseItem(): Promise<void> {
    this.syncFeedbackToEntity();
    this.cleanNavigationBackReferences();

    const savedItem = await this.save();
    if (savedItem) {
      this.itemSaved.emit(savedItem);
      this.closeModal.emit();
    }
  }

  onStatusChange(newStatus: number): void {
    const currentEntity = this.editingEntity();
    if (currentEntity) {
      const newEntity = Object.assign(this.getEmptyEntity(), currentEntity);
      newEntity.status = newStatus;
      this.editingEntity.set(newEntity);
    }
  }

  get feedBack(): Feedback {
    const visit = this.editingEntity() as Visit;
    if (!visit.feedbacks || visit.feedbacks.length === 0) {
      visit.feedbacks = [new Feedback()];
      visit.feedbacks[0].fK_Visit = visit.id;
      visit.feedbacks[0].fK_PointOfService = visit.fK_PointOfService;
    }
    return visit.feedbacks[0];
  }

  updateFeedbackScore(score: number): void {
    this.suggestion.set(score.toString());
    this.comments.set(this.comments());
  }

  updateFeedbackComment(comment: string): void {
    this.comments.set(comment);
  }

  onDateTimePropretyChange(date: Date | null, propretyName: 'startDate' | 'endDate'): void {
    const entity = this.editingEntity();
    if (entity) {
      entity[propretyName] = date ?? undefined;
      const newEntity = Object.assign(Object.create(Object.getPrototypeOf(entity)), entity);
      this.editingEntity.set(newEntity);
    }
  }

}
